import { useState, useEffect } from 'react';
import { Backdrop, Theme, Typography, Button, Grid, CircularProgress, LinearProgress, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getStyleVariables, opacities } from 'styles/vars';
import { GVIcon } from 'components/icons';
import {
  getInspectionStarted,
  getDifferencesLoadedSelector,
  getInspectionIdle,
  getSearchingForGraphic,
  inspection,
  getLoadingDifferences,
  getResyncLoading,
  getProcessingFlattenedText,
  files,
  getInspectionId,
  getStartingInspection,
  getInspectionFailed,
  getShapesJobStatus,
  getInspectionSettings,
  getProcessingResyncResults,
} from 'store';
import cx from 'classnames';
import { AutoPrepJobStatus, DocumentTypes, FileStatus } from 'types';
import { cancelInspection } from 'store/request/inspections/actions';
import { makeStyles } from 'tss-react/mui';
import { useAutoPrepStatusStateStore } from 'zstore/autoPrepStatusStateStore';
import { useReportState, ReportStates, REPORT_DOWNLOAD_OVERLAY_Z_INDEX } from 'zstore/reportStore';
import { axiosInstance } from 'store/store';
import { getBackendURL, appendToken } from 'store/interceptors';
import { useFlags } from 'launchdarkly-react-client-sdk';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    backdrop: {
      zIndex: theme.zIndex.tooltip + 1,
      backgroundColor: styleVariables.global.backdropColor,
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    title: {
      marginLeft: theme.spacing(1),
    },
    icon: {
      width: theme.spacing(3.5),
      height: 'auto',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    red: {
      color: theme.palette.secondary.main,
      marginRight: theme.spacing(1),
    },
    progressContainer: {
      width: '400px',
      textAlign: 'center',
      marginTop: theme.spacing(3),
      height: theme.spacing(10),
    },
    linearProgress: {
      width: theme.spacing(37.5),
      height: theme.spacing(0.5),
      marginTop: theme.spacing(1),
    },
    progressText: {
      color: theme.palette.text.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      whiteSpace: 'pre-wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    warningText: {
      color: theme.palette.warning.main,
      marginTop: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.5),
    },
    white: {
      color: theme.palette.text.primary,
    },
    disabled: {
      opacity: opacities.disabled,
    },
  };
});

const ResultsLoadingBackdrop = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const searching = useSelector(getSearchingForGraphic);
  const reloadingDifferences = useSelector(getLoadingDifferences);
  const resyncLoading = useSelector(getResyncLoading);
  const processingResyncResults = useSelector(getProcessingResyncResults);
  const startingInspection = useSelector(getStartingInspection);
  const inspectionStarted = useSelector(getInspectionStarted);
  const inspectionIdle = useSelector(getInspectionIdle);
  const inspectionFailed = useSelector(getInspectionFailed);
  const differenceLoaded = useSelector(getDifferencesLoadedSelector);
  const ocrStatus = useSelector(getProcessingFlattenedText);
  const fetchingShape = useSelector(getShapesJobStatus) === FileStatus.inprogress;
  const { autoPrepStatus, setAutoPrepStatus } = useAutoPrepStatusStateStore();
  const fetchingAutoPrep =
    autoPrepStatus === AutoPrepJobStatus.inprogress || autoPrepStatus === AutoPrepJobStatus.pending;
  const inspectionId = useSelector(getInspectionId);
  const processingFlattenedText = Object.values(ocrStatus).includes(true);
  const [cancellingDisabled, setCancellingDisabled] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [displayText, setDisplayText] = useState<string>('');
  const [buttonText, setButtonText] = useState<string>('');
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const settings = useSelector(getInspectionSettings);
  const { reportState, isMultiInspection } = useReportState();
  const generatingReport = reportState === ReportStates.Generating || reportState === ReportStates.Downloading;
  const featureFlags = useFlags();
  const showRuntimeEstimation = Boolean(featureFlags.showRuntimeEstimation);

  useEffect(() => {
    if (processingResyncResults) {
      setDisplayText('Cleaning up the results list...');
      setCancellingDisabled(true);
    }
  }, [processingResyncResults]);

  useEffect(() => {
    if (
      !startingInspection ||
      !inspectionStarted ||
      !searching ||
      !reloadingDifferences ||
      !processingFlattenedText ||
      !resyncLoading
    ) {
      setCancelling(false);
      setCancellingDisabled(false);
    }
  }, [startingInspection, inspectionStarted, searching, reloadingDifferences, resyncLoading]);

  useEffect(() => {
    if (
      startingInspection ||
      inspectionStarted ||
      searching ||
      reloadingDifferences ||
      processingFlattenedText ||
      fetchingShape ||
      fetchingAutoPrep ||
      resyncLoading ||
      generatingReport
    ) {
      setOpen(true);
    } else if (
      inspectionIdle ||
      differenceLoaded ||
      inspectionFailed ||
      (!generatingReport && !startingInspection && !searching)
    ) {
      // check if inspection got cancelled or if we got the results
      setCancelling(false);
      setOpen(false);
    }
    if (searching) {
      setDisplayText('Verify Compare is locating the matching image...');
      setButtonText('Search');
    } else if (startingInspection || inspectionStarted) {
      setDisplayText('Inspecting your files...');
      setButtonText('Inspection');
      setCancellingDisabled(!inspectionStarted);
    } else if (reloadingDifferences) {
      setDisplayText('Generating inspection results');
      setButtonText('Process');
    } else if (processingFlattenedText) {
      setDisplayText('Converting your file to live text...');
      setButtonText('Process');
    } else if (fetchingShape) {
      setDisplayText('Auto-prepping selection...');
      setButtonText('Process');
    } else if (fetchingAutoPrep) {
      setDisplayText('Matching template to artwork…');
      setButtonText('Process');
    } else if (resyncLoading) {
      setDisplayText('Re-inspecting the selected area...');
      setButtonText('Re-inspect');
    } else if (generatingReport) {
      setDisplayText(isMultiInspection ? 'Generating multi-inspection report...' : 'Generating report...');
      setButtonText('Report');
      setCancellingDisabled(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inspectionStarted,
    searching,
    differenceLoaded,
    inspectionIdle,
    reloadingDifferences,
    processingFlattenedText,
    startingInspection,
    generatingReport,
    isMultiInspection,
    fetchingShape,
    fetchingAutoPrep,
    resyncLoading,
  ]);

  useEffect(() => {
    if (!showRuntimeEstimation) {
      setEstimatedTime(null);
      return;
    }

    const fetchEstimatedTime = async () => {
      try {
        const response = await axiosInstance.post(
          getBackendURL('inspections/runtime-estimation'),
          { inspectionId, settings },
          appendToken({}),
        );
        if (response.data > 0) {
          setEstimatedTime(response.data);
        } else {
          setEstimatedTime(null);
        }
      } catch (error) {
        setEstimatedTime(null);
        console.error('Error fetching estimated time:', error);
      }
    };

    if ((startingInspection || resyncLoading) && settings) {
      fetchEstimatedTime();
    }
  }, [startingInspection, resyncLoading, settings, inspectionId, showRuntimeEstimation]);

  // Track elapsed time
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (inspectionStarted || resyncLoading) {
      setElapsedTime(0);
      intervalId = setInterval(() => {
        setElapsedTime((prev) => prev + 1000);
      }, 1000);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [inspectionStarted, resyncLoading]);

  // Update displayText for resync based on progress
  useEffect(() => {
    if (resyncLoading && estimatedTime && elapsedTime >= 0) {
      const newDisplayText = getResyncDisplayText(elapsedTime, estimatedTime);
      if (newDisplayText !== displayText) {
        setDisplayText(newDisplayText);
      }
    }
  }, [resyncLoading, estimatedTime, elapsedTime]);

  const getProgressValue = () => {
    if (!estimatedTime || estimatedTime <= 0) return 0;
    const progress = (elapsedTime / estimatedTime) * 100;
    return Math.min(progress, 100);
  };
  const getInspectionProgressMessage = (elapsedTime: number, estimatedTime: number): string => {
    // Convert ms to seconds for easier comparison
    const elapsedSeconds = elapsedTime / 1000;

    // Early stages messaging
    if (elapsedSeconds <= 3) {
      return 'Starting inspection…';
    } else if (elapsedSeconds <= 7) {
      return 'Preparing files…';
    } else if (elapsedSeconds <= 15) {
      return 'Reviewing documents…';
    } else if (elapsedSeconds <= 20) {
      return 'Analyzing structures…';
    } else if (elapsedSeconds <= 25) {
      return 'Estimating time remaining…';
    }

    // Within the estimated time
    if (elapsedTime <= estimatedTime) {
      const minutesLeft = Math.floor((estimatedTime - elapsedTime) / 60000);
      const secondsLeft = Math.ceil(((estimatedTime - elapsedTime) % 60000) / 1000);
      if (minutesLeft > 0) {
        return `About ${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''} and ${secondsLeft} second${
          secondsLeft !== 1 ? 's' : ''
        } left`;
      } else {
        return `About ${secondsLeft} second${secondsLeft !== 1 ? 's' : ''} left`;
      }
    }

    const tipMessage =
      '\n\nTips💡: Reduce inspection scope — exclude non-critical pages, use the crop tool or remove layers for a faster inspection.';

    // Exceeding estimated time scenarios
    const minutesOverEstimate = Math.floor((elapsedTime - estimatedTime) / 60000);

    if (minutesOverEstimate <= 1) {
      return 'Double-checking details — taking a bit longer than expected.';
    } else if (minutesOverEstimate <= 3) {
      return 'Still working thoroughly. Thank you for waiting.';
    } else if (minutesOverEstimate <= 5) {
      return "We're still at it — thank you for your patience." + tipMessage;
    } else if (minutesOverEstimate <= 10) {
      return 'Still processing carefully — apologies for the wait. If this seems stuck, you can cancel or contact support.';
    } else {
      return 'This is unusually long; the process may be stuck. We recommend trying again with fewer inclusions.';
    }
  };

  const getResyncDisplayText = (elapsedTime: number, estimatedTime: number): string => {
    if (!estimatedTime || estimatedTime <= 0) {
      return 'Re-inspecting the selected area...';
    }
    const firstThird = estimatedTime / 3;
    const secondThird = (estimatedTime * 2) / 3;
    if (elapsedTime <= firstThird) {
      return 'Re-inspecting the selected area...';
    } else if (elapsedTime <= secondThird) {
      return 'Checking for differences...';
    } else {
      return 'Cleaning up the results list...';
    }
  };

  const handleCancelLoading = () => {
    setCancelling(true);
    if (searching) {
      dispatch(inspection.actions.setSearchingForZone(false));
    } else if (reloadingDifferences) {
      dispatch(inspection.actions.setLoadingDifferences(false));
    } else if (processingFlattenedText) {
      dispatch(
        files.actions.setOCRStatus({
          documentType: ocrStatus.source ? DocumentTypes.source : DocumentTypes.target,
          status: '',
        }),
      );
    } else if (fetchingShape) {
      dispatch(files.actions.setShapesJobStatus(FileStatus.cancelled));
    } else if (fetchingAutoPrep) {
      setAutoPrepStatus(AutoPrepJobStatus.cancelled);
    } else if (resyncLoading) {
      dispatch(inspection.actions.setResyncCancelled(true));
    } else {
      dispatch(cancelInspection(inspectionId));
    }
  };

  return (
    <Backdrop
      open={open}
      className={cx(classes.backdrop, { backdropClosed: !open })}
      style={generatingReport ? { zIndex: REPORT_DOWNLOAD_OVERLAY_Z_INDEX } : undefined}
      data-testid="inspection_loading_backdrop"
    >
      <Grid container direction="column" sx={{ alignItems: 'center', position: 'relative', pt: 8 }}>
        <Box>
          <Grid className={classes.titleContainer}>
            <GVIcon className={classes.icon} />
            <Typography className={classes.title} variant="h4">
              {displayText}
            </Typography>
          </Grid>
          <Grid item className={classes.progressContainer}>
            <Grid>
              {(inspectionStarted || resyncLoading) && estimatedTime ? (
                <Box alignItems={'center'} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                  <LinearProgress
                    variant={elapsedTime < estimatedTime ? 'determinate' : 'indeterminate'}
                    value={getProgressValue()}
                    className={classes.linearProgress}
                    color="secondary"
                  />
                  <Typography className={classes.progressText}>
                    {inspectionStarted && !resyncLoading
                      ? getInspectionProgressMessage(elapsedTime, estimatedTime)
                      : null}
                  </Typography>
                </Box>
              ) : (
                <Grid item container justifyContent="center">
                  <CircularProgress />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item className={classes.buttonContainer}>
            <Button onClick={handleCancelLoading} disabled={cancellingDisabled || cancelling}>
              <span
                className={cx(classes.red, {
                  [classes.disabled]: cancellingDisabled,
                })}
              >
                {cancelling ? 'Cancelling' : 'Cancel'}
              </span>
              <span
                className={cx(classes.white, {
                  [classes.disabled]: cancellingDisabled,
                })}
              >
                {buttonText}
                {cancelling ? ' ...' : ''}
              </span>
            </Button>
          </Grid>
        </Box>
      </Grid>
    </Backdrop>
  );
};

export default ResultsLoadingBackdrop;
